import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Camera, CheckCircle2, ArrowLeft, LogOut, X, Clock, ShieldCheck, AlertTriangle, MapPin, PenTool } from "lucide-react";
import { fileUrl } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authApi = (method, path, body, opts = {}) => {
  const token = localStorage.getItem("cps_token");
  return axios({
    method, url: `${API}${path}`, data: body,
    headers: token ? { Authorization: `Bearer ${token}`, ...(opts.headers || {}) } : (opts.headers || {}),
    ...(opts.config || {}),
  }).then(r => r.data);
};

const STATUS_LBL = {
  pendente: { lbl: "Aguardando CAMASA", color: "#d4b27a", bg: "rgba(212,178,122,0.15)", icon: Clock },
  aprovada: { lbl: "Aprovada", color: "#5fa86c", bg: "rgba(95,168,108,0.15)", icon: CheckCircle2 },
  rejeitada: { lbl: "Rejeitada — reenviar", color: "#c47a7a", bg: "rgba(196,122,122,0.15)", icon: AlertTriangle },
};

function StatusBadge({ status }) {
  const m = STATUS_LBL[status] || STATUS_LBL.pendente;
  const Icon = m.icon;
  return (
    <span
      data-testid={`status-${status}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "4px 12px", border: `1px solid ${m.color}55`, background: m.bg,
        color: m.color, fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase", fontWeight: 600,
      }}
    >
      <Icon size={11} /> {m.lbl}
    </span>
  );
}


/* =========================================================================
 * PARCEIRO PORTAL — rota /parceiro
 * ========================================================================= */
export default function ParceiroPortal() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [authPhase, setAuthPhase] = useState("init"); // init | authing | ready | denied

  // Device-token auth + PWA manifest override quando em /parceiro
  useEffect(() => {
    // Swap manifest p/ versão PWA do parceiro
    const link = document.querySelector('link[rel="manifest"]');
    const original = link?.getAttribute("href");
    if (link) link.setAttribute("href", "/parceiro-manifest.json");
    return () => {
      if (link && original) link.setAttribute("href", original);
    };
  }, []);

  // Device-token auth: tablet abre /parceiro?token=xxx → troca por JWT permanente
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dt = params.get("token");
    if (dt) {
      setAuthPhase("authing");
      axios.get(`${API}/parceiro/auth?token=${encodeURIComponent(dt)}`)
        .then(r => {
          localStorage.setItem("cps_token", r.data.token);
          // Limpa o token da URL (não fica exposto no histórico do navegador)
          window.history.replaceState({}, "", "/parceiro");
          setAuthPhase("ready");
        })
        .catch(() => {
          localStorage.removeItem("cps_token");
          setAuthPhase("denied");
        });
    } else if (localStorage.getItem("cps_token")) {
      setAuthPhase("ready");
    } else {
      setAuthPhase("denied");
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [meRes, obrasRes] = await Promise.all([
        authApi("get", "/parceiro/me"),
        authApi("get", "/parceiro/obras"),
      ]);
      setMe(meRes);
      setObras(obrasRes.items || []);
    } catch (e) {
      // Token expirado/inválido → cai pra tela "denied"
      if (e?.response?.status === 401) {
        localStorage.removeItem("cps_token");
        setAuthPhase("denied");
      } else {
        setErr(e?.response?.data?.detail || e?.message || "Erro ao carregar");
      }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (authPhase === "ready") load(); }, [authPhase, load]);

  const logout = () => {
    localStorage.removeItem("cps_token");
    setAuthPhase("denied");
  };

  if (authPhase === "init" || authPhase === "authing") {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--bg-1)" }}>
        <div style={{ fontSize: 13, letterSpacing: ".2em", color: "var(--gold)", textTransform: "uppercase" }}>
          Autenticando tablet…
        </div>
      </div>
    );
  }
  if (authPhase === "denied") {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--bg-1)", padding: 24 }} data-testid="parceiro-denied">
        <div style={{ textAlign: "center", maxWidth: 460 }}>
          <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--gold)", textTransform: "uppercase" }}>CAMASA · Portal do Parceiro</div>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 30, marginTop: 14, color: "var(--ink-1)" }}>
            Acesso não autorizado
          </h2>
          <p style={{ marginTop: 14, color: "var(--ink-3)", fontSize: 14, lineHeight: 1.6 }}>
            Este tablet não está autenticado. Contate a <strong>CAMASA</strong> para receber o link de acesso correto.
          </p>
        </div>
      </div>
    );
  }
  if (loading) return <div style={{ padding: 60, color: "var(--ink-3)" }}>Carregando…</div>;
  if (err) return <div style={{ padding: 60, color: "#c47a7a" }}>{err}</div>;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-1)" }} data-testid="parceiro-portal">
      <header style={{ padding: "18px 26px", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: ".3em", color: "var(--gold)", textTransform: "uppercase" }}>CAMASA · Portal do Parceiro</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 22, color: "var(--ink-1)", marginTop: 4 }}>
            {me?.terceirizado?.empresa || me?.terceirizado?.nome || me?.user?.nome || "Parceiro"}
          </div>
        </div>
        <button onClick={logout} className="btn btn-ghost btn-sm" data-testid="parceiro-logout">
          <LogOut size={12} /> Sair
        </button>
      </header>

      <div style={{ padding: 26, maxWidth: 1100, margin: "0 auto" }}>
        <h2 style={{ fontSize: 14, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--ink-2)", marginBottom: 18 }}>
          Suas obras ativas
        </h2>

        {obras.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--ink-3)", border: "1px dashed var(--line)" }}>
            Nenhuma obra vinculada a você no momento.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
            {obras.map(o => (
              <div
                key={o.id}
                onClick={() => navigate(`/parceiro/obras/${o.id}`)}
                data-testid={`parceiro-obra-${o.id}`}
                style={{
                  padding: 18, background: "var(--bg-2)", border: "1px solid var(--line)",
                  cursor: "pointer", transition: "all 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--gold)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--line)"; }}
              >
                <div style={{ fontSize: 10, letterSpacing: ".22em", color: "var(--gold)", textTransform: "uppercase" }}>{o.codigo}</div>
                <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 18, marginTop: 4, color: "var(--ink-1)" }}>
                  {o.nome}
                </div>
                <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>Cliente: {o.cliente_nome || "—"}</div>
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  {o.series_count?.pendente > 0 && (
                    <span style={{ fontSize: 10, color: "#d4b27a", padding: "3px 8px", background: "rgba(212,178,122,0.12)", border: "1px solid rgba(212,178,122,0.35)", letterSpacing: ".1em", textTransform: "uppercase" }}>
                      {o.series_count.pendente} aguardando
                    </span>
                  )}
                  {o.series_count?.aprovada > 0 && (
                    <span style={{ fontSize: 10, color: "#5fa86c", padding: "3px 8px", background: "rgba(95,168,108,0.12)", border: "1px solid rgba(95,168,108,0.35)", letterSpacing: ".1em", textTransform: "uppercase" }}>
                      {o.series_count.aprovada} aprovadas
                    </span>
                  )}
                  {o.series_count?.rejeitada > 0 && (
                    <span style={{ fontSize: 10, color: "#c47a7a", padding: "3px 8px", background: "rgba(196,122,122,0.12)", border: "1px solid rgba(196,122,122,0.35)", letterSpacing: ".1em", textTransform: "uppercase" }}>
                      {o.series_count.rejeitada} rejeitadas
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* =========================================================================
 * PARCEIRO OBRA DETALHE — rota /parceiro/obras/:id
 * ========================================================================= */
export function ParceiroObraDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [obra, setObra] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openForm, setOpenForm] = useState(null); // { etapa_nome }

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [r, m] = await Promise.all([
        authApi("get", `/parceiro/obras/${id}`),
        authApi("get", "/parceiro/me"),
      ]);
      setObra(r);
      setMe(m);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Erro");
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 60 }}>Carregando…</div>;
  if (err) return <div style={{ padding: 60, color: "#c47a7a" }}>{err}</div>;
  if (!obra) return null;

  const series = obra.series_fotos || [];
  // Filtra etapas pertinentes ao perfil do parceiro logado.
  // PRIORIDADE 1: lista customizada por Carlo em terceirizado.etapas_visiveis (admin override).
  // PRIORIDADE 2: fallback regex por tipo (marmoraria, frentista, fornecedor).
  // SEM override e SEM tipo mapeado: mostra todas (fallback seguro).
  const tipoParceiro = me?.terceirizado?.tipo;
  const customList = me?.terceirizado?.etapas_visiveis;
  const ETAPAS_POR_TIPO = {
    marmoraria: [/frete.*marmor/i, /produ[cç][aã]o/i, /frete.*obra/i],
    frentista: [/frete/i],
    fornecedor: [/sele[cç][aã]o|materi[ai]is|compra/i],
  };
  const padroesRelevantes = ETAPAS_POR_TIPO[tipoParceiro];
  const usandoCustom = Array.isArray(customList);
  let etapasFiltradas;
  if (usandoCustom) {
    const normalized = customList.map(s => (s || "").toLowerCase().trim());
    etapasFiltradas = (obra.etapas || []).filter(e => normalized.includes((e.nome || "").toLowerCase().trim()));
  } else if (padroesRelevantes) {
    etapasFiltradas = (obra.etapas || []).filter(e => padroesRelevantes.some(p => p.test(e.nome || "")));
  } else {
    etapasFiltradas = obra.etapas || [];
  }
  const totalEtapasObra = (obra.etapas || []).length;
  const filtradoCount = etapasFiltradas.length;
  const byEtapa = etapasFiltradas.map(e => ({
    ...e,
    series: series.filter(s => s.etapa_nome === e.nome),
  }));

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-1)" }} data-testid="parceiro-obra-detail">
      <header style={{ padding: "18px 26px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 14 }}>
        <button onClick={() => navigate("/parceiro")} className="btn btn-ghost btn-sm" data-testid="parceiro-back">
          <ArrowLeft size={12} /> Voltar
        </button>
        <div>
          <div style={{ fontSize: 10, letterSpacing: ".22em", color: "var(--gold)" }}>{obra.codigo}</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 22, color: "var(--ink-1)" }}>{obra.nome}</div>
          <div style={{ fontSize: 11, color: "var(--ink-3)" }}>Cliente: {obra.cliente_nome || "—"}</div>
        </div>
      </header>

      <div style={{ padding: 26, maxWidth: 1100, margin: "0 auto" }}>
        <h2 style={{ fontSize: 14, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--ink-2)", marginBottom: 14 }}>
          Etapas — registre as fotos de cada item
        </h2>
        {(padroesRelevantes || usandoCustom) && (
          <div data-testid="parceiro-etapas-filtro" style={{
            marginBottom: 14, padding: "10px 14px",
            background: "rgba(184,147,90,0.08)", border: "1px solid rgba(184,147,90,0.25)",
            color: "var(--ink-2)", fontSize: 12, letterSpacing: ".05em",
          }}>
            Mostrando <strong>{filtradoCount} etapa(s)</strong>
            {usandoCustom
              ? <> definidas pela CAMASA para <strong>{me?.terceirizado?.nome}</strong></>
              : <> pertinentes ao perfil <strong>{tipoParceiro}</strong></>}
            {totalEtapasObra > filtradoCount && (
              <span style={{ color: "var(--ink-3)" }}> · obra possui {totalEtapasObra} etapas no total</span>
            )}
          </div>
        )}
        {byEtapa.length === 0 ? (
          <div style={{
            padding: 32, background: "var(--bg-2)", border: "1px dashed var(--line)",
            color: "var(--ink-3)", fontSize: 13, textAlign: "center",
          }}>
            Nenhuma etapa desta obra está dentro do seu perfil ({tipoParceiro || "—"}) ainda.
            Quando uma etapa pertinente for criada, ela aparece aqui.
          </div>
        ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {byEtapa.map((e, i) => (
            <div key={i} data-testid={`parceiro-etapa-${i}`} style={{ padding: 18, background: "var(--bg-2)", border: "1px solid var(--line)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 10, letterSpacing: ".18em", color: "var(--ink-3)", textTransform: "uppercase" }}>Etapa {String(i + 1).padStart(2, "0")}</div>
                  <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 18, color: "var(--ink-1)" }}>{e.nome}</div>
                </div>
                <button
                  className="btn btn-sm"
                  onClick={() => setOpenForm({ etapa_nome: e.nome })}
                  data-testid={`btn-nova-serie-${i}`}
                  style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }}
                >
                  <Camera size={13} /> Registrar item desta etapa
                </button>
              </div>

              {e.series.length > 0 ? (
                <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
                  {e.series.map(s => (
                    <div key={s.id} data-testid={`serie-card-${s.id}`} style={{ padding: 12, background: "var(--bg-3)", border: "1px solid var(--line)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, gap: 8 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "var(--ink-1)", wordBreak: "break-word", flex: 1, minWidth: 0 }}>{s.item_descricao}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <StatusBadge status={s.status} />
                          <button
                            onClick={async () => {
                              if (!window.confirm("Apagar esta série de fotos? Esta ação não pode ser desfeita.")) return;
                              try {
                                await authApi("delete", `/obras/${obra.id}/series_fotos/${s.id}`);
                                load();
                              } catch (e) {
                                alert("Erro ao apagar: " + (e?.response?.data?.detail || e.message));
                              }
                            }}
                            data-testid={`serie-delete-${s.id}`}
                            title="Apagar série inteira"
                            style={{ padding: 4, border: "1px solid var(--line)", background: "transparent", color: "#c47a7a", cursor: "pointer" }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min((s.fotos || []).length, 3)}, 1fr)`, gap: 4 }}>
                        {(s.fotos || []).map((f, idx) => (
                          <div key={idx} style={{
                            aspectRatio: "1/1",
                            backgroundImage: `url(${fileUrl(f.url)})`,
                            backgroundSize: "cover", backgroundPosition: "center",
                            border: "1px solid var(--line)",
                            position: "relative",
                          }} title={`Imagem ${idx + 1}`}>
                            <div style={{ position: "absolute", bottom: 2, left: 2, padding: "1px 5px", background: "rgba(0,0,0,0.7)", color: "var(--gold)", fontSize: 9, letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 600 }}>
                              {idx + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 6, textTransform: "uppercase", letterSpacing: ".1em" }}>
                        {(s.fotos || []).length} imagem{(s.fotos || []).length === 1 ? "" : "ns"}
                      </div>
                      {s.status === "rejeitada" && s.motivo_rejeicao && (
                        <div style={{ fontSize: 11, marginTop: 8, padding: 8, background: "rgba(196,122,122,0.08)", color: "#d49595", border: "1px solid rgba(196,122,122,0.3)" }}>
                          <strong>Motivo:</strong> {s.motivo_rejeicao}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ marginTop: 12, fontSize: 12, color: "var(--ink-3)", fontStyle: "italic" }}>
                  Nenhum item registrado nesta etapa ainda.
                </div>
              )}
            </div>
          ))}
        </div>
        )}
      </div>

      {openForm && (
        <SerieFotosModal
          obraId={obra.id}
          etapaNome={openForm.etapa_nome}
          parceiroNome={me?.terceirizado?.empresa || me?.terceirizado?.nome || me?.user?.nome || ""}
          onClose={() => setOpenForm(null)}
          onSent={() => { setOpenForm(null); load(); }}
        />
      )}
    </div>
  );
}


/* =========================================================================
 * MODAL DE UPLOAD DE SÉRIE — fotos variáveis (1+) + GPS obrigatório + assinatura digital
 * Deploy 98: array dinâmico de fotos (botão + para adicionar, ✕ para remover),
 * labels "Imagem 1, 2, 3..." em vez de Frente/Direita/Esquerda.
 * ========================================================================= */
function SerieFotosModal({ obraId, etapaNome, parceiroNome, onClose, onSent }) {
  const [itemDesc, setItemDesc] = useState("");
  const [fotos, setFotos] = useState([]); // [{ file, previewUrl }]
  // GPS state
  const [gps, setGps] = useState(null); // {lat, lon, endereco}
  const [gpsErr, setGpsErr] = useState("");
  const [gpsBusy, setGpsBusy] = useState(false);
  // Signature state
  const [hasSign, setHasSign] = useState(false);
  const [assinante, setAssinante] = useState(parceiroNome || "");
  const sigRef = useRef(null);
  const sigDrawing = useRef(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Picker novo (adiciona N fotos ao array — input com multiple)
  const inputAdd = useRef(null);
  const handleAddFotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const novas = files.slice(0, 12 - fotos.length).map(f => ({
      file: f,
      previewUrl: URL.createObjectURL(f),
    }));
    setFotos(prev => [...prev, ...novas]);
    if (inputAdd.current) inputAdd.current.value = "";
  };
  const removerFoto = (idx) => {
    setFotos(prev => {
      const out = [...prev];
      const [removed] = out.splice(idx, 1);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return out;
    });
  };

  /* ------------- GPS capture + reverse geocode ------------- */
  const capturarGps = () => {
    setGpsErr("");
    setGpsBusy(true);
    if (!navigator.geolocation) {
      setGpsErr("Este dispositivo não suporta GPS");
      setGpsBusy(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        let endereco = `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
        try {
          const r = await axios.get(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=pt-BR&zoom=12`, {
            headers: { "User-Agent": "CAMASA-CPS/1.0" },
          });
          const a = r.data?.address || {};
          const cidade = a.city || a.town || a.village || a.municipality || a.suburb;
          const estado = a.state_code || a.state;
          endereco = [cidade, estado].filter(Boolean).join(", ") || r.data?.display_name || endereco;
        } catch { /* fallback para lat/lon brutos */ }
        setGps({ lat, lon, endereco });
        setGpsBusy(false);
      },
      (e) => {
        setGpsErr(
          e.code === 1
            ? "Permissão de GPS negada. Ative o GPS nas configurações do tablet e tente novamente."
            : "Não foi possível obter sua localização. Tente novamente."
        );
        setGpsBusy(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  };

  /* ------------- Canvas signature ------------- */
  useEffect(() => {
    const canvas = sigRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    // Set internal resolution para HiDPI
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#d4b27a";
  }, []);

  const sigPoint = (e) => {
    const canvas = sigRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = (e.touches && e.touches[0]) || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const sigStart = (e) => {
    e.preventDefault();
    const canvas = sigRef.current;
    const ctx = canvas.getContext("2d");
    const p = sigPoint(e);
    sigDrawing.current = true;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const sigMove = (e) => {
    if (!sigDrawing.current) return;
    e.preventDefault();
    const canvas = sigRef.current;
    const ctx = canvas.getContext("2d");
    const p = sigPoint(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasSign) setHasSign(true);
  };
  const sigEnd = () => { sigDrawing.current = false; };
  const sigClear = () => {
    const canvas = sigRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSign(false);
  };

  const enviar = async () => {
    setErr("");
    if (!itemDesc.trim()) { setErr("Informe o nome do item (ex: Bancada Banheiro)"); return; }
    if (fotos.length < 1) {
      setErr("Envie pelo menos 1 foto");
      return;
    }
    if (!gps) {
      setErr("A localização é obrigatória. Ative o GPS e tente novamente.");
      return;
    }
    if (!hasSign) { setErr("Assinatura obrigatória. Desenhe sua assinatura no campo."); return; }
    if (!assinante.trim()) { setErr("Informe quem está assinando."); return; }
    setBusy(true);
    try {
      const canvas = sigRef.current;
      const sigBase64 = canvas.toDataURL("image/png");
      const fd = new FormData();
      fd.append("etapa_nome", etapaNome);
      fd.append("item_descricao", itemDesc.trim());
      fotos.forEach(f => fd.append("fotos", f.file));
      fd.append("latitude", String(gps.lat));
      fd.append("longitude", String(gps.lon));
      fd.append("endereco_aproximado", gps.endereco);
      fd.append("assinatura_base64", sigBase64);
      fd.append("assinado_por", assinante.trim());
      const token = localStorage.getItem("cps_token");
      await axios.post(`${API}/obras/${obraId}/series_fotos`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSent();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Erro ao enviar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-back" onClick={onClose} data-testid="serie-fotos-modal">
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 760, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>
        <div className="modal-head">
          <h3 className="modal-title">Registrar item — <em>{etapaNome}</em></h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* 1. Descrição */}
          <div className="field" style={{ marginBottom: 14 }}>
            <label>Descrição do item *</label>
            <input
              className="input"
              value={itemDesc}
              onChange={e => setItemDesc(e.target.value.slice(0, 120))}
              placeholder="Ex: Bancada Banheiro, Bancada Cozinha, Soleira Entrada"
              data-testid="serie-item-desc"
              autoFocus
            />
          </div>
          {/* 2. Fotos — grade dinâmica (1+ fotos, máx 12) */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {fotos.map((f, idx) => (
              <div key={idx} style={{ position: "relative", aspectRatio: "1/1", border: "2px solid var(--gold)" }} data-testid={`serie-foto-${idx}`}>
                <img src={f.previewUrl} alt={`Imagem ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <div style={{ position: "absolute", bottom: 4, left: 4, padding: "2px 6px", background: "rgba(0,0,0,0.7)", color: "var(--gold)", fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 600 }}>
                  Imagem {idx + 1}
                </div>
                <button
                  onClick={() => removerFoto(idx)}
                  data-testid={`serie-remover-${idx}`}
                  style={{ position: "absolute", top: 4, right: 4, padding: 4, background: "rgba(0,0,0,0.7)", color: "white", border: 0, cursor: "pointer" }}
                  title="Remover esta foto"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {fotos.length < 12 && (
              <button
                onClick={() => inputAdd.current?.click()}
                data-testid="serie-add-foto"
                style={{
                  aspectRatio: "1/1", border: "2px dashed var(--gold)",
                  background: "rgba(184,147,90,0.05)", color: "var(--gold)",
                  cursor: "pointer", display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", gap: 4,
                }}
              >
                <Camera size={24} />
                <span style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase" }}>
                  {fotos.length === 0 ? "Adicionar foto" : "+ Mais"}
                </span>
              </button>
            )}
            <input
              ref={inputAdd}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              style={{ display: "none" }}
              onChange={handleAddFotos}
              data-testid="serie-input-add"
            />
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: "var(--ink-3)" }}>
            {fotos.length} foto{fotos.length === 1 ? "" : "s"} selecionada{fotos.length === 1 ? "" : "s"} · pode adicionar até 12
          </div>

          {/* 3. GPS */}
          <div style={{ marginTop: 18, padding: 12, border: "1px solid var(--line)", background: "var(--bg-3)" }} data-testid="gps-block">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <MapPin size={14} color="var(--gold)" />
              <span style={{ fontSize: 11, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--ink-2)", fontWeight: 600 }}>
                Localização (obrigatória)
              </span>
            </div>
            {gps ? (
              <div style={{ fontSize: 13, color: "var(--ink-1)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <span data-testid="gps-endereco"><strong>📍 {gps.endereco}</strong></span>
                <button onClick={capturarGps} className="btn btn-ghost btn-sm">Atualizar</button>
              </div>
            ) : (
              <button onClick={capturarGps} disabled={gpsBusy} data-testid="gps-capture" className="btn btn-sm" style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }}>
                <MapPin size={12} /> {gpsBusy ? "Obtendo GPS…" : "Capturar localização atual"}
              </button>
            )}
            {gpsErr && <div style={{ marginTop: 8, fontSize: 12, color: "#d49595" }} data-testid="gps-err">{gpsErr}</div>}
          </div>

          {/* 4. Signature */}
          <div style={{ marginTop: 14, padding: 12, border: "1px solid var(--line)", background: "var(--bg-3)" }} data-testid="sig-block">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <PenTool size={14} color="var(--gold)" />
                <span style={{ fontSize: 11, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--ink-2)", fontWeight: 600 }}>
                  Assinatura digital (obrigatória)
                </span>
              </div>
              <button onClick={sigClear} className="btn btn-ghost btn-sm" data-testid="sig-clear">Limpar</button>
            </div>
            <input
              className="input"
              value={assinante}
              onChange={e => setAssinante(e.target.value.slice(0, 80))}
              placeholder="Nome de quem assina (ex: João Silva — Pic Stone)"
              style={{ marginBottom: 8 }}
              data-testid="sig-nome"
            />
            <canvas
              ref={sigRef}
              data-testid="sig-canvas"
              style={{ width: "100%", height: 140, background: "rgba(255,255,255,0.95)", border: "1px dashed var(--gold)", touchAction: "none", cursor: "crosshair" }}
              onMouseDown={sigStart}
              onMouseMove={sigMove}
              onMouseUp={sigEnd}
              onMouseLeave={sigEnd}
              onTouchStart={sigStart}
              onTouchMove={sigMove}
              onTouchEnd={sigEnd}
            />
            <div style={{ marginTop: 4, fontSize: 10, color: "var(--ink-3)" }}>
              Desenhe sua assinatura para confirmar a execução do serviço.
            </div>
          </div>

          {err && <div style={{ marginTop: 12, padding: 10, background: "rgba(196,122,122,0.08)", border: "1px solid rgba(196,122,122,0.3)", color: "#d49595", fontSize: 13 }} data-testid="serie-err">⚠ {err}</div>}
          <div style={{ marginTop: 12, padding: 10, background: "rgba(184,147,90,0.06)", border: "1px solid rgba(184,147,90,0.25)", fontSize: 12, color: "var(--ink-3)" }}>
            <ShieldCheck size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 6, color: "var(--gold)" }} />
            As fotos passam por <strong>aprovação da CAMASA</strong> antes de aparecer ao cliente. GPS e assinatura ficam registrados como prova de execução.
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button
            className="btn"
            onClick={enviar}
            disabled={busy}
            data-testid="serie-enviar"
            style={{ background: "var(--gold)", color: "#0a0a0a", fontWeight: 600 }}
          >
            {busy ? "Enviando…" : "Enviar para aprovação"}
          </button>
        </div>
      </div>
    </div>
  );
}
